import { HeartFilledLikeIcon, HeartLikeIcon } from "@/assets/icons";
import {
  useLibrary,
  useToggleSaveAlbum,
  useToggleSaveTrack,
} from "@/lib/lutely-api/hooks";
import { saveRedirect } from "@/lib/storage-utils";
import { cn } from "@/lib/utils";
import { Button, IconButton } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";

export type HeartButtonProps = {
  kind: "track" | "album";
  id: string;
  /** Icon color. Omit to inherit the current text color (style via className). */
  color?: string;
  iconSize?: number;
  /** Render as a labelled Save/Saved pill instead of a bare icon button. */
  withLabel?: boolean;
  className?: string;
};

/**
 * Save/unsave heart toggle for tracks and albums. Optimistic — the icon flips
 * immediately and rolls back if the request fails. Logged-out taps open the
 * signup modal instead of silently no-oping.
 */
export default function HeartButton({
  kind,
  id,
  color,
  iconSize = 18,
  withLabel = false,
  className,
}: HeartButtonProps) {
  const navigate = useNavigate();
  const { isLoggedIn, isTrackSaved, isAlbumSaved } = useLibrary();
  const { mutate: toggleSaveTrack } = useToggleSaveTrack();
  const { mutate: toggleSaveAlbum } = useToggleSaveAlbum();

  const saved = kind === "track" ? isTrackSaved(id) : isAlbumSaved(id);
  const label = saved ? `Remove ${kind} from saved` : `Save ${kind}`;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Hearts sit inside clickable cards — don't trigger play/navigation.
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      saveRedirect(window.location.href);
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, auth: "signup" as const }),
      });
      return;
    }

    if (kind === "track") {
      toggleSaveTrack({ trackId: id });
    } else {
      toggleSaveAlbum({ albumId: id });
    }
  };

  const heartIcon = saved ? (
    <HeartFilledLikeIcon width={iconSize} height={iconSize} />
  ) : (
    <HeartLikeIcon fill="currentColor" width={iconSize} height={iconSize} />
  );

  if (withLabel) {
    return (
      <Button
        variant="outline"
        onClick={handleClick}
        aria-pressed={saved}
        className={cn(
          "flex items-center gap-1.5 flex-shrink-0 text-sm font-semibold leading-[140%] px-4 h-[34px] rounded-full transition-all hover:brightness-90 cursor-pointer",
          className,
        )}
        style={
          color ? { color, boxShadow: `inset 0 0 0 1px ${color}` } : undefined
        }
      >
        {heartIcon}
        {saved ? "Saved" : "Save"}
      </Button>
    );
  }

  return (
    <IconButton
      variant="ghost"
      radius="full"
      size="2"
      onClick={handleClick}
      aria-label={label}
      aria-pressed={saved}
      className={cn(
        "flex-shrink-0 h-[34px] w-[34px] rounded-full cursor-pointer",
        className,
      )}
      style={color ? { color } : undefined}
    >
      {heartIcon}
    </IconButton>
  );
}
